import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { LocaleProvider } from './contexts/LocaleContext'
import { OrganizationProvider, useOrganization } from './contexts/OrganizationContext'
import { getOrgSlug, isPlatformHost } from './api/tenant'
import OrgPickerPage from './pages/OrgPickerPage'
import LoginPage from './pages/LoginPage'
import VolunteerSignupPage from './pages/VolunteerSignupPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ConfirmEmailPage from './pages/ConfirmEmailPage'
import AcceptInvitationPage from './pages/AcceptInvitationPage'
import VolunteerApprovalPage from './pages/VolunteerApprovalPage'
import PendingApprovalPage from './pages/PendingApprovalPage'
import CompleteVolunteerApplicationPage from './pages/CompleteVolunteerApplicationPage'
import CompleteProfilePage from './pages/CompleteProfilePage'
import DashboardPage from './pages/DashboardPage'
import VolunteersPage from './pages/VolunteersPage'
import ShiftsPage from './pages/ShiftsPage'
import MyShiftsPage from './pages/MyShiftsPage'
import MessagesPage from './pages/MessagesPage'
import EventsPage from './pages/EventsPage'
import EventDetailPage from './pages/EventDetailPage'
import ProfilePage from './pages/ProfilePage'
import SystemUpdatesPage from './pages/SystemUpdatesPage'
import AdminTodoPage from './pages/AdminTodoPage'
import CheckInPage from './pages/CheckInPage'
import OrgSettingsPage from './pages/admin/OrgSettingsPage'
import ShiftBuilderPage from './pages/admin/ShiftBuilderPage'
import EventBuilderPage from './pages/admin/EventBuilderPage'
import MatchingPage from './pages/admin/MatchingPage'
import EventDayPage from './pages/admin/EventDayPage'
import Layout from './components/Layout'
import LoadingSpinner from './components/LoadingSpinner'
import ErrorBoundary from './components/ErrorBoundary'
import PlatformShell from './pages/platform/PlatformShell'
import PlatformOrgsPage from './pages/platform/PlatformOrgsPage'
import PlatformOrgNewPage from './pages/platform/PlatformOrgNewPage'
import PlatformOrgPage from './pages/platform/PlatformOrgPage'
import PlatformImportPage from './pages/platform/PlatformImportPage'
import PlatformEmailPage from './pages/platform/PlatformEmailPage'


const ProtectedRoute: React.FC<{ children: React.ReactNode; requireVolunteer?: boolean; requireAdmin?: boolean }> = ({ 
  children, 
  requireVolunteer = false,
  requireAdmin = false
}) => {
  const { user, isVolunteer, isAdmin, isPendingApproval, needsVolunteerApplication, needsProfileCompletion, loading } = useAuth()

  console.log('ProtectedRoute render:', { 
    loading, 
    user: !!user, 
    needsProfileCompletion, 
    isPendingApproval, 
    needsVolunteerApplication,
    isAdmin,
    requireVolunteer,
    requireAdmin
  })


  if (loading) {
    return <LoadingSpinner />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (needsProfileCompletion) {
    console.log('ProtectedRoute: redirecting to complete-profile')
    return <Navigate to="/complete-profile" replace />
  }

  // Skip volunteer flow for admins
  if (isAdmin) {
    console.log('ProtectedRoute: admin detected, skipping volunteer flow')
    return <>{children}</>
  }

  if (isPendingApproval) {
    console.log('ProtectedRoute: redirecting to pending-approval')
    return <Navigate to="/pending-approval" replace />
  }

  if (needsVolunteerApplication) {
    console.log('ProtectedRoute: redirecting to complete-volunteer-application')
    return <Navigate to="/complete-volunteer-application" replace />
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  if (requireVolunteer && !isVolunteer && !isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

const AppRoutes: React.FC = () => {
  const { user, loading } = useAuth()


  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <Routes>
      <Route path="/login" element={!user ? <LoginPage /> : <Navigate to={isPlatformHost() ? '/platform' : '/dashboard'} replace />} />
      <Route path="/signup" element={!user ? <VolunteerSignupPage /> : <Navigate to="/dashboard" replace />} />
      <Route path="/reset-password" element={!user ? <ResetPasswordPage /> : <Navigate to="/dashboard" replace />} />
      <Route path="/forgot-password" element={!user ? <ForgotPasswordPage /> : <Navigate to="/dashboard" replace />} />
      <Route path="/confirm-email" element={<ConfirmEmailPage />} />
      {/* Open to signed-in visitors too: an existing account can be invited to a second organisation. */}
      <Route path="/invite" element={<AcceptInvitationPage />} />
      <Route path="/" element={<Navigate to={isPlatformHost() ? '/platform' : '/dashboard'} replace />} />
      <Route
        path="/platform"
        element={
          <PlatformShell>
            <PlatformOrgsPage />
          </PlatformShell>
        }
      />
      <Route
        path="/platform/new"
        element={
          <PlatformShell>
            <PlatformOrgNewPage />
          </PlatformShell>
        }
      />
      <Route
        path="/platform/import"
        element={
          <PlatformShell>
            <PlatformImportPage />
          </PlatformShell>
        }
      />
      <Route
        path="/platform/email"
        element={
          <PlatformShell>
            <PlatformEmailPage />
          </PlatformShell>
        }
      />
      <Route
        path="/platform/organizations/:id"
        element={
          <PlatformShell>
            <PlatformOrgPage />
          </PlatformShell>
        }
      />
      <Route
        path="/events"
        element={
          <ProtectedRoute>
            <Layout>
              <EventsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/new"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <EventBuilderPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:eventId/edit"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <EventBuilderPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/events/:eventId"
        element={
          <ProtectedRoute>
            <Layout>
              <EventDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/volunteers"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <VolunteersPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shifts"
        element={
          <ProtectedRoute requireVolunteer>
            <Layout>
              <ShiftsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      {['/messages', '/messages/:conversationId'].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute requireVolunteer>
              <Layout>
                <MessagesPage />
              </Layout>
            </ProtectedRoute>
          }
        />
      ))}
      <Route
        path="/my-shifts"
        element={
          <ProtectedRoute requireVolunteer>
            <Layout>
              <MyShiftsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      {/* The check-in surface is deliberately outside Layout: it is the .rs-night
          full-screen view a volunteer opens at the gate, not a page in the portal. */}
      <Route
        path="/check-in"
        element={
          <ProtectedRoute requireVolunteer>
            <CheckInPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shifts/new"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <ShiftBuilderPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shifts/:shiftId/edit"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <ShiftBuilderPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/shifts/:shiftId/matching"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <MatchingPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/event-day"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <EventDayPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/organisation"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <OrgSettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/volunteer-approval"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <VolunteerApprovalPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/system-updates"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <SystemUpdatesPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin-todo"
        element={
          <ProtectedRoute requireAdmin>
            <Layout>
              <AdminTodoPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/pending-approval"
        element={<PendingApprovalPage />}
      />
      <Route
        path="/complete-profile"
        element={<CompleteProfilePage />}
      />
      <Route
        path="/complete-volunteer-application"
        element={<CompleteVolunteerApplicationPage />}
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <ProfilePage />
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}

/**
 * Routes that carry their own context in a token and so work on any host, including
 * one that names no organisation. An invitation mail can be opened anywhere.
 */
const TENANTLESS_ROUTES = ['/invite', '/confirm-email', '/platform']

/**
 * Every screen past this point assumes an organisation: the branding, the roster and
 * the shifts are all per-tenant. Without one there is nothing coherent to render, so
 * ask which organisation is meant rather than showing an empty portal.
 */
const OrgGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { organization, loading } = useOrganization()
  const { pathname } = useLocation()

  if (loading) {
    return <LoadingSpinner />
  }

  const onTenantlessRoute = TENANTLESS_ROUTES.some((route) => pathname.startsWith(route))

  if (!organization && !onTenantlessRoute && !isPlatformHost()) {
    return <OrgPickerPage attempted={getOrgSlug()} />
  }

  return <>{children}</>
}

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <LocaleProvider>
        <OrganizationProvider>
          <AuthProvider>
            <Router>
              <OrgGate>
                <div className="min-h-screen bg-surface-page">
                  <AppRoutes />
                </div>
              </OrgGate>
            </Router>
          </AuthProvider>
        </OrganizationProvider>
      </LocaleProvider>
    </ErrorBoundary>
  )
}

export default App
